import { Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEvents } from "api/events";
import { AdvancedSettingsBar } from "components/Campaign/CampaignEffects/AdvancedSettingsBar/AdvancedSettingsBar";
import { prepareCleanExpression } from "components/EffectSearch/utils";
import {
  Condition,
  ConditionReadOnly,
} from "components/Layout/Campaign/Common/CampaignRules/Rule/Conditions/ConditionReadOnly";
import { NewFormatConditionReadOnly } from "components/Layout/Campaign/Common/CampaignRules/Rule/Conditions/NewFormatConditions/NewFormatConditionReadOnly";
import { EffectReadOnly } from "components/Layout/Campaign/Common/CampaignRules/Rule/Effects/EffectReadOnly";
import { generateRules } from "components/Layout/Campaign/Common/CampaignRules/rulesHelpers";
import { isNewFormat } from "components/Layout/Campaign/Common/campaignHelpers";
import { useMessage } from "hooks/intl";
import {
  CampaignConditionNewFormat,
  CampaignFEValues,
  CampaignRuleTarget,
  CampaignTriggers,
  CampaignTypes,
  ConditionType,
  TransactionCampaigns,
} from "types/campaigns";
import { useStyles } from "../styles";

export function Rules({
  campaignData,
  errors,
}: {
  campaignData: CampaignFEValues;
  errors: any;
}) {
  const classes = useStyles({});
  const { message } = useMessage();
  const { useCustomEventSchemaDetails } = useEvents();
  const isInternalEventTrigger =
    campaignData && campaignData.trigger === CampaignTriggers.INTERNAL_EVENT;

  const isReferralType =
    campaignData && campaignData.type === CampaignTypes.REFERRAL;

  const { data: selectedSchemaDetails } = useCustomEventSchemaDetails(
    !isInternalEventTrigger && "event" in campaignData
      ? campaignData?.event
      : undefined,
  );

  const rules = campaignData
    ? generateRules({
        rules: campaignData.rules,
        campaignTrigger: campaignData.trigger,
        campaignType: campaignData.type,
        selectedSchemaDetails,
      })
    : [];

  return (
    <Grid container className={classes.gridContainer}>
      <Grid className={classes.gridChild} size={{ xs: 12 }}>
        {rules?.map((rule, index) => (
          <div className={classes.rule} key={index}>
            <span
              className={`${classes.ruleHeader} ${index === 0 ? classes.firstRule : ""}`}
            >
              <Typography className={classes.ruleTitle}>
                {rule.name || `${message({ id: "rule" })} ${index + 1}`}
              </Typography>
              {rule.description && (
                <Typography className={classes.ruleDesc}>
                  {rule.description}
                </Typography>
              )}
            </span>
            <span className={classes.ruleContent}>
              <span className={classes.conditionsHeader}>
                {message({ id: "conditions" })}
              </span>
              <span>
                {rule?.conditions?.map((condition, conditionNumber) => {
                  const dataTestId = `campaign-rule-${
                    index + 1
                  }-condition-${conditionNumber + 1}`;
                  const error =
                    errors?.rules?.[index]?.conditions?.[conditionNumber];

                  return (
                    <>
                      <div className={classes.readOnlyWrapper}>
                        <span>{conditionNumber + 1}.</span>
                        {isNewFormat(
                          condition as
                            | ConditionType
                            | CampaignConditionNewFormat,
                        ) ? (
                          <NewFormatConditionReadOnly
                            conditionObject={
                              condition as CampaignConditionNewFormat
                            }
                            transactionItemsFilters={
                              (campaignData as TransactionCampaigns)
                                ?.transactionItemsFilters
                            }
                            data-test-id={dataTestId}
                            error={error}
                          />
                        ) : (
                          <ConditionReadOnly
                            condition={
                              {
                                ...condition,
                                target: "target" in rule && rule.target,
                              } as Condition & { target?: CampaignRuleTarget }
                            }
                            isRefferalCampaign={!!isReferralType}
                            data-test-id={dataTestId}
                            error={error}
                          />
                        )}
                      </div>
                    </>
                  );
                })}
              </span>
            </span>
            <span className={classes.ruleContent}>
              <span className={classes.conditionsHeader}>
                {message({ id: "effects" })}
              </span>
              <span>
                {rule?.effects?.map((effect, effectNumber) => {
                  const effectDataTestId = `campaign-rule-${
                    index + 1
                  }-effect-${effectNumber + 1}`;
                  const expirationStrategy =
                    effect.unitsExpirationRule?.expirationStrategy;
                  const lockStrategy = effect.unitsLockRule?.lockStrategy;
                  const cleanExpirationExpression =
                    expirationStrategy === "never_expires"
                      ? message({ id: "neverExpires" })
                      : prepareCleanExpression(
                          effect.unitsExpirationRule?.expression as string,
                        );
                  const cleanPendingExpression =
                    lockStrategy === "no_pending"
                      ? message({ id: "noPending" })
                      : prepareCleanExpression(
                          effect.unitsLockRule?.expression as string,
                        );
                  const labels = [
                    {
                      label: message({ id: "expirationSettings" }),
                      value: cleanExpirationExpression,
                    },
                    {
                      label: message({ id: "pendingSettings" }),
                      value: cleanPendingExpression,
                    },
                  ];
                  const error = errors?.rules?.[index]?.effects?.[effectNumber];

                  return (
                    <div className={`${classes.readOnlyContainer}`}>
                      <div className={classes.readOnlyWrapper}>
                        <span>{effectNumber + 1}.</span>
                        <EffectReadOnly
                          effect={effect}
                          data-test-id={effectDataTestId}
                          error={error}
                        />
                      </div>
                      {cleanExpirationExpression || cleanPendingExpression ? (
                        <AdvancedSettingsBar
                          labels={labels}
                          advancedSettingsClass={classes.advancedSettings}
                          vectorMarginClass={classes.vectorMargin}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </span>
            </span>
          </div>
        ))}
      </Grid>
    </Grid>
  );
}
